import React, { useCallback, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { Button } from '../../../../../../../components/Button/Button'
import { DescriptorType, Filter, FilterExpression, FilterGroup, GroupOperator, OPERATOR_OPTIONS } from '../../../../../../../models/Account'
import { FormFieldDefinition, IntegrationObjectRelationship } from '../../../hooks'
import { IntegrationServiceId } from '../../../../../../../common/integrations'
import { FilterCard } from './FilterCard'
import { EnforcedFilterCard } from './EnforcedFilterCard'
import { FilterCondition, FilterCardState } from './types'

const FILTER_VALUE_VARIABLES = ['currentUser', 'currentUserName']

export interface FilterGroupBuilderProps {
  serviceId: IntegrationServiceId
  value?: FilterGroup | null
  onChange: (value: FilterGroup | null) => void
  availableFormFieldDefinitions: FormFieldDefinition[]
  availableRelationships?: IntegrationObjectRelationship[]
  loading?: boolean
  placeholders: FilterExpression[]
}

const defaultFilterName = (index: number): string => `Filter group ${index + 1}`

const emptyCondition = (): FilterCondition => ({
  id: crypto.randomUUID(),
  reference: null,
  operator: null,
  value: '',
  enforced: false,
})

const mapValueToCards = (group: FilterGroup | null | undefined): FilterCardState[] => {
  if (!group || !group.filters || group.filters.length === 0) {
    return []
  }
  return group.filters.map((filter, index) => ({
    id: crypto.randomUUID(),
    name: filter.name || defaultFilterName(index),
    conditions: (filter.expressions || []).map((exp) => ({
      id: crypto.randomUUID(),
      reference: exp.field,
      operator: exp.operator,
      value: exp.source?.value ?? '',
      valueType: exp.valueType ?? null,
      valueFormat: exp.valueFormat ?? null,
      enforced: false,
    })),
  }))
}

export const FilterGroupBuilder: React.FC<FilterGroupBuilderProps> = ({
  serviceId,
  value,
  onChange,
  availableFormFieldDefinitions,
  availableRelationships,
  loading,
  placeholders,
}) => {
  const isDataPresent = !!value && (value.filters?.length ?? 0) > 0

  const [cards, setCards] = useState<FilterCardState[]>(() => mapValueToCards(value))
  const [hasInitialized, setHasInitialized] = useState<boolean>(isDataPresent)

  if (!hasInitialized && isDataPresent) {
    setCards(mapValueToCards(value))
    setHasInitialized(true)
  }

  const buildFilterGroup = useCallback((currentCards: FilterCardState[]): FilterGroup | null => {
    const isVariableValue = (val: string) => FILTER_VALUE_VARIABLES.includes(val)

    const filters: Filter[] = currentCards
      .map((card): Filter | null => {
        const validConditions = card.conditions.filter((c) => c.reference && c.operator && c.value !== '' && c.value !== null && c.value !== undefined)
        if (validConditions.length === 0) {
          return null
        }
        return {
          name: card.name,
          groupOperator: GroupOperator.AND,
          expressions: validConditions.map((c) => ({
            field: c.reference!,
            operator: c.operator!,
            source: { type: isVariableValue(c.value) ? DescriptorType.VARIABLE : DescriptorType.STATIC, value: c.value, valueType: 'string' },
            valueType: c.valueType ?? undefined,
            valueFormat: c.valueFormat ?? undefined,
          })),
        }
      })
      .filter((f): f is Filter => f !== null)

    if (filters.length === 0) {
      return null
    }
    return { groupOperator: GroupOperator.OR, filters }
  }, [])

  const triggerChange = (newCards: FilterCardState[]) => {
    setCards(newCards)
    if (!hasInitialized) {
      setHasInitialized(true)
    }
    onChange(buildFilterGroup(newCards))
  }

  const operatorOptions = useMemo(() => OPERATOR_OPTIONS.map((op) => ({ id: op.id, label: op.label })), [])

  const handleAddFilter = () => {
    triggerChange([...cards, { id: crypto.randomUUID(), name: defaultFilterName(cards.length), conditions: [emptyCondition()] }])
  }

  const handleRemoveFilter = (cardId: string) => {
    triggerChange(cards.filter((card) => card.id !== cardId))
  }

  const handleNameChange = (cardId: string, name: string) => {
    triggerChange(cards.map((card) => (card.id === cardId ? { ...card, name } : card)))
  }

  const handleAddCondition = (cardId: string) => {
    triggerChange(cards.map((card) => (card.id === cardId ? { ...card, conditions: [...card.conditions, emptyCondition()] } : card)))
  }

  const handleRemoveCondition = (cardId: string, index: number) => {
    triggerChange(
      cards.map((card) => {
        if (card.id !== cardId) {
          return card
        }
        const conditions = [...card.conditions]
        conditions.splice(index, 1)
        return { ...card, conditions }
      }),
    )
  }

  const handleUpdateCondition = (cardId: string, index: number, patch: Partial<FilterCondition>) => {
    triggerChange(
      cards.map((card) => {
        if (card.id !== cardId) {
          return card
        }
        const conditions = [...card.conditions]
        const current = conditions[index]
        if (!current) {
          return card
        }
        conditions[index] = { ...current, ...patch }
        return { ...card, conditions }
      }),
    )
  }

  return (
    <Container className='p-0'>
      <EnforcedFilterCard conditions={placeholders} />

      {cards.map((card) => (
        <FilterCard
          key={card.id}
          serviceId={serviceId}
          name={card.name}
          conditions={card.conditions}
          availableFormFieldDefinitions={availableFormFieldDefinitions}
          availableRelationships={availableRelationships}
          operatorOptions={operatorOptions}
          loading={loading}
          onNameChange={(name) => handleNameChange(card.id, name)}
          onConditionChange={(index, patch) => handleUpdateCondition(card.id, index, patch)}
          onAddCondition={() => handleAddCondition(card.id)}
          onRemoveCondition={(index) => handleRemoveCondition(card.id, index)}
          onRemove={() => handleRemoveFilter(card.id)}
        />
      ))}

      <Row>
        <Col>
          <Button type='button' variant='primaryGhost' className='border-0' onClick={handleAddFilter}>
            + Add new filter
          </Button>
        </Col>
      </Row>
    </Container>
  )
}
